/**
 * Lifecycle derivation and display helpers for governance actions.
 *
 * The indexer exposes four independent timestamps (`ratified`, `enacted`,
 * `expired`, `dropped`), each `{ epoch, time }` and each nullable. A proposal
 * with no timestamps set is still open for voting. Real distribution across the
 * 155 mainnet proposals: 81 enacted, 70 expired, 2 dropped, 2 active.
 */

export type ProposalState =
  | 'active'
  | 'ratified'
  | 'enacted'
  | 'expired'
  | 'dropped';

export interface ProposalStatusStamp {
  epoch: number | null;
  time: string | null;
}

export interface ProposalStatus {
  ratified?: ProposalStatusStamp | null;
  enacted?: ProposalStatusStamp | null;
  expired?: ProposalStatusStamp | null;
  dropped?: ProposalStatusStamp | null;
}

export interface IndexedProposal {
  id: string;
  proposal: {
    type: string;
    details?: Record<string, unknown> | null;
    metadataUrl?: string | null;
    metadataHash?: string | null;
  };
  meta?: {
    title?: string | null;
    abstract?: string | null;
    references?: unknown;
  } | null;
  createdAt?: {
    time?: string | null;
    epoch?: number | null;
    /** Submitting transaction — joins this proposal to `<tx>#<index>` votes. */
    tx?: string | null;
    index?: number | null;
  } | null;
  expireAt?: { time?: string | null; epoch?: number | null } | null;
  status?: ProposalStatus | null;
}

const STATE_ORDER: ProposalState[] = [
  'enacted',
  'ratified',
  'expired',
  'dropped',
];

export function deriveProposalState(proposal: IndexedProposal): ProposalState {
  const status = proposal.status || {};
  for (const state of STATE_ORDER) {
    if (status[state as keyof ProposalStatus]?.time) return state;
  }
  // No terminal stamp yet — fall back to the expiry epoch so a proposal that
  // has run out of time isn't advertised as open while the indexer catches up.
  const expiry = proposal.expireAt?.time;
  if (expiry && new Date(expiry).getTime() < Date.now()) return 'expired';
  return 'active';
}

/** Epoch/time at which the proposal reached its current state, if terminal. */
export function stateStamp(
  proposal: IndexedProposal
): ProposalStatusStamp | null {
  const status = proposal.status || {};
  for (const state of STATE_ORDER) {
    const stamp = status[state as keyof ProposalStatus];
    if (stamp?.time) return stamp;
  }
  return null;
}

export const STATE_LABEL: Record<ProposalState, string> = {
  active: 'Open for voting',
  ratified: 'Ratified',
  enacted: 'Enacted',
  expired: 'Expired',
  dropped: 'Dropped',
};

/** Short label used in title tags, where every character competes for space. */
export const STATE_SHORT: Record<ProposalState, string> = {
  active: 'Voting open',
  ratified: 'Ratified',
  enacted: 'Enacted',
  expired: 'Expired',
  dropped: 'Dropped',
};

/**
 * Wording for the status pill on list rows and cards.
 *
 * Same as `STATE_SHORT` except for `active`, which reads "Live" to match the
 * interactive governance actions table; "Voting open" is kept for title tags,
 * where the extra words earn their place.
 */
export const STATE_PILL: Record<ProposalState, string> = {
  ...STATE_SHORT,
  active: 'Live',
};

const TYPE_LABEL: Record<string, string> = {
  TreasuryWithdrawals: 'Treasury Withdrawal',
  InfoAction: 'Info Action',
  ParameterChange: 'Parameter Change',
  NewCommittee: 'Constitutional Committee Update',
  UpdateCommittee: 'Constitutional Committee Update',
  NewConstitution: 'New Constitution',
  HardForkInitiation: 'Hard Fork Initiation',
  NoConfidence: 'No Confidence',
};

export function proposalTypeLabel(type?: string | null): string {
  if (!type) return 'Governance Action';
  return TYPE_LABEL[type] || type.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// No `proposalTypeSlug` here on purpose. Deriving a hub slug from the on-chain
// type name produced `info-action`, `parameter-change`, `hard-fork-initiation`
// and `new-committee`, none of which are hub slugs, so the type breadcrumb and
// hub link disappeared for every action except treasury withdrawals. Ask
// `hubForType` in `lib/seo/hubs.ts` instead; it matches on the hubs' own
// predicates and so cannot drift from the routes that actually exist.

export function lovelaceToAdaLabel(lovelace?: unknown): string | null {
  const value =
    typeof lovelace === 'string' ? Number(lovelace) : (lovelace as number);
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const ada = value / 1_000_000;
  return `${ada.toLocaleString('en-US', { maximumFractionDigits: 0 })} ₳`;
}

/**
 * One-line summary a search engine can show under the title: type, state, and
 * — for treasury withdrawals, the majority of all proposals — the amount.
 */
export function proposalFacts(proposal: IndexedProposal): string {
  const state = deriveProposalState(proposal);
  const parts: string[] = [proposalTypeLabel(proposal.proposal?.type)];

  const amount = lovelaceToAdaLabel(
    (proposal.proposal?.details as Record<string, unknown>)?.['Amount']
  );
  if (amount) parts.push(`requesting ${amount}`);

  parts.push(STATE_LABEL[state].toLowerCase());

  const stamp = stateStamp(proposal);
  if (stamp?.epoch) {
    parts.push(`in epoch ${stamp.epoch}`);
  } else if (state === 'active' && proposal.expireAt?.epoch) {
    parts.push(`until epoch ${proposal.expireAt.epoch}`);
  }

  return parts.join(' · ');
}

/** Collapse whitespace and truncate on a word boundary for meta descriptions. */
export function truncate(text: string, max = 155): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > max * 0.6 ? lastSpace : max).trim()}…`;
}
